import dayjs from 'dayjs';

/**
 * Verifica si un valor tiene contenido válido para filtros
 * @param {*} value - Valor a verificar
 * @returns {boolean} - True si el valor es válido
 */
export function hasValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return value === true;
  return !!value;
}

/**
 * Crea tags para un facet específico basado en su tipo y valor
 * @param {Object} facet - Definición del facet
 * @param {*} value - Valor del filtro
 * @param {string} key - Clave del filtro
 * @returns {Array} - Array de tags generados
 */
export function createTagsForFacet(facet, value, key) {
  const { input_type: inputType, results, show_name } = facet;

  if (inputType === 'toggle') {
    return [{
      key,
      value: facet.name,
      inputType,
      showName: show_name,
    }];
  }

  if (inputType === 'radio') {
    let effectiveValue = null;

    if (typeof value === 'string' && value !== '') {
      effectiveValue = value;
    } else if (Array.isArray(value) && value.length === 1 && typeof value[0] === 'string' && value[0] !== '') {
      effectiveValue = value[0];
    } else if (Array.isArray(value) && value.length === 0) {
      return [];
    }

    if (effectiveValue === null) {
      return [];
    }

    const result = results?.find(res => res.value === effectiveValue);
    return result ? [{
      key,
      value: effectiveValue,
      inputType,
      showName: result.show_name,
    }] : [];
  }

  if (results?.length && Array.isArray(value)) {
    return results
      .filter(res => value.includes(res.value))
      .map(res => ({
        key,
        value: res.value,
        inputType,
        showName: res.show_name,
      }));
  }

  return [];
}

/**
 * Procesa filtros de precio de manera consistente
 * @param {*} price - Valor del precio
 * @returns {*} - Precio procesado
 */
export function processPriceFilter(price) {
  if (typeof price === 'string') {
    return [price];
  }
  
  if (price && typeof price === 'object') {
    const { min_price = 0, max_price = 0 } = price;
    return min_price === 0 && max_price === 0 ? [] : { from: min_price, to: max_price };
  }
  
  return price || [];
}

/**
 * Crea tags especiales para fecha y hora (específico para catalogFilters)
 * @param {Object} searchByDateTimeConfig - Configuración de fecha/hora
 * @returns {Array} - Array de tags especiales
 */
export function createSpecialDateTimeTags(searchByDateTimeConfig) {
  const specialTags = [];
  
  // Tag para fecha seleccionada
  if (searchByDateTimeConfig.selectedDate) {
    const date = dayjs(searchByDateTimeConfig.selectedDate);
    const formattedDate = date.format('dddd DD/MM');
    const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    
    specialTags.push({
      key: 'date',
      value: searchByDateTimeConfig.selectedDate,
      inputType: 'date',
      showName: capitalizedDate,
    });
  }
  
  // Tag para horario seleccionado
  if (searchByDateTimeConfig.timeRangeSlug && searchByDateTimeConfig.allTimeRanges.length) {
    const timeRange = searchByDateTimeConfig.allTimeRanges.find(
      range => range.slug === searchByDateTimeConfig.timeRangeSlug
    );
    
    if (timeRange) {
      specialTags.push({
        key: 'time_range_slug',
        value: searchByDateTimeConfig.timeRangeSlug,
        inputType: 'time_range',
        showName: `${timeRange.start} - ${timeRange.end}`,
      });
    }
  }

  return specialTags;
}

/**
 * Verifica si los filtros tienen valores válidos
 * @param {Object} filters - Objeto de filtros
 * @returns {boolean} - True si hay filtros válidos
 */
export function hasValidFilters(filters) {
  return Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
    return !!value;
  });
}

/**
 * Opciones estandarizadas para clearCurrentFilters
 * @typedef {Object} ClearFiltersOptions
 * @property {boolean} clearCategory - Limpiar categorías
 * @property {boolean} clearDateTime - Limpiar fecha y hora
 * @property {boolean} refetch - Hacer refetch después de limpiar
 */

/**
 * Sistema unificado de callbacks de URL
 */
class URLCallbackManager {
  constructor() {
    this.callbacks = new Map();
  }

  register(callback, namespace = 'default') {
    this.callbacks.set(namespace, callback);
    
    // Para compatibilidad con código existente
    if (typeof window !== 'undefined') {
      if (namespace === 'filters') {
        window.filtersUpdateURL = callback;
      } else if (namespace === 'catalogFilters') {
        window.catalogFiltersUpdateURL = callback;
      }
    }
  }

  unregister(namespace = 'default') {
    this.callbacks.delete(namespace);
    
    // Para compatibilidad con código existente
    if (typeof window !== 'undefined') {
      if (namespace === 'filters') {
        delete window.filtersUpdateURL;
      } else if (namespace === 'catalogFilters') {
        delete window.catalogFiltersUpdateURL;
      }
    }
  }

  trigger(namespace = 'default', ...args) {
    const callback = this.callbacks.get(namespace);
    if (callback) {
      callback(...args);
    }
  }

  // Métodos de compatibilidad
  markFiltersURLUpdateNeeded(...args) {
    this.trigger('filters', ...args);
  }

  markCatalogFiltersURLUpdateNeeded(...args) {
    this.trigger('catalogFilters', ...args);
  }
}

// Instancia singleton del manager
export const urlCallbackManager = new URLCallbackManager(); 